import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ShieldCheck, ShieldAlert, CheckCircle2, AlertCircle, Clock, ArrowLeft,
  UserRound, FileSignature, Car, ShieldCheck as ShieldIcon, Camera,
  Siren, PhoneCall, ClipboardList,
} from "lucide-react";
import { api } from "@/lib/api";

const STATUS_STYLES = {
  complete:       { label: "Complete",       cls: "bg-teal-100 text-teal-800 border-teal-200", Icon: CheckCircle2 },
  pending:        { label: "Pending",        cls: "bg-amber-100 text-amber-800 border-amber-200", Icon: Clock },
  expiring_soon:  { label: "Expiring Soon",  cls: "bg-orange-100 text-orange-800 border-orange-200", Icon: AlertCircle },
  action_required:{ label: "Action Required",cls: "bg-red-100 text-red-700 border-red-200", Icon: ShieldAlert },
  info:           { label: "Reference",      cls: "bg-slate-100 text-slate-700 border-slate-200", Icon: ClipboardList },
};

function StatusBadge({ status, testid }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.info;
  const Icon = s.Icon;
  return (
    <Badge className={`${s.cls} border font-mono text-[10px] uppercase inline-flex items-center gap-1`} data-testid={testid}>
      <Icon className="w-3 h-3" />{s.label}
    </Badge>
  );
}

function Row({ Icon, title, description, status, statusTestId, actionLabel, onAction, meta, testid }) {
  return (
    <Card className="border-slate-200" data-testid={testid}>
      <CardContent className="p-5 flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex items-start gap-3 flex-1">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
            status === "complete" ? "bg-teal-50 text-teal-700" :
            status === "action_required" ? "bg-red-50 text-red-600" :
            status === "expiring_soon" ? "bg-orange-50 text-orange-600" :
            status === "pending" ? "bg-amber-50 text-amber-600" :
            "bg-slate-100 text-slate-600"
          }`}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="font-display font-bold text-slate-900">{title}</div>
              <StatusBadge status={status} testid={statusTestId} />
            </div>
            <div className="text-sm text-slate-600 mt-1">{description}</div>
            {meta && <div className="text-xs text-slate-500 mt-1 font-mono">{meta}</div>}
          </div>
        </div>
        {actionLabel && onAction && (
          <Button onClick={onAction} variant="outline" className="border-teal-300 text-teal-700 hover:bg-teal-50" data-testid={`${testid}-action`}>
            {actionLabel}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

// Insurance is "expiring soon" if it expires within 30 days.
function daysUntil(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d)) return null;
  return Math.floor((d - new Date()) / (1000 * 60 * 60 * 24));
}

export default function CleanerSafetyChecklist() {
  const nav = useNavigate();
  const [eligibility, setEligibility] = useState(null);
  const [profile, setProfile] = useState(null);
  const [vehicle, setVehicle] = useState(null);
  const [insurance, setInsurance] = useState([]);
  const [emergency, setEmergency] = useState(null);
  const [agreementStatus, setAgreementStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [e, pr, v, ins, ec, agrSt] = await Promise.all([
        api.get("/cleaner/eligibility"),
        api.get("/cleaner/profile"),
        api.get("/cleaner/vehicle").catch(() => ({ data: null })),
        api.get("/cleaner/insurance").catch(() => ({ data: [] })),
        api.get("/cleaner/emergency-contact").catch(() => ({ data: null })),
        api.get("/cleaner/agreement/status").catch(() => ({ data: null })),
      ]);
      setEligibility(e.data);
      setProfile(pr.data);
      setVehicle(v.data);
      setInsurance(ins.data || []);
      setEmergency(ec.data);
      setAgreementStatus(agrSt.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load safety checklist");
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const items = useMemo(() => {
    if (!eligibility) return [];
    const e = eligibility;
    const goRisk = () => nav("/cleaner/onboarding-checklist");
    const goOnboarding = () => nav("/cleaner/onboarding");

    // Latest active insurance (approved) — for expiration status.
    const activeIns = insurance.find(i => i.status === "approved");
    const insDays = daysUntil(activeIns?.expiration_date);
    const dlDays = daysUntil(e.drivers_license_expiration);

    const rows = [
      {
        testid: "check-identity",
        Icon: UserRound,
        title: "Identity / ID verification",
        description: "Your government-issued ID must be reviewed by CLEANBAZ before you can accept jobs.",
        status: e.identity_ok ? "complete" : (e.identity_status === "pending_id_review" ? "pending" : "action_required"),
        statusTestId: "check-identity-status",
        meta: `Current status: ${e.identity_status || "not submitted"}`,
        actionLabel: e.identity_ok ? null : "Complete ID upload",
        onAction: e.identity_ok ? null : goOnboarding,
      },
      {
        testid: "check-agreement",
        Icon: FileSignature,
        title: "CLEANBAZ Cleaner Agreement",
        description: "You must accept the current version of the Cleaner Agreement to be eligible for jobs.",
        status: e.agreement_ok ? "complete" : "action_required",
        statusTestId: "check-agreement-status",
        meta: agreementStatus?.accepted
          ? `Accepted · version ${agreementStatus.current_version}`
          : `Current version ${e.agreement_current_version || agreementStatus?.current_version || "—"} not yet accepted`,
        actionLabel: e.agreement_ok ? null : "Review & accept",
        onAction: e.agreement_ok ? null : goRisk,
      },
      {
        testid: "check-vehicle",
        Icon: Car,
        title: "Vehicle information",
        description: e.transportation_type === "own_vehicle"
          ? "Vehicle year, make, plate, state, and driver's license on file."
          : "You've declared a non-vehicle transportation type. Vehicle info not required unless you switch to own vehicle.",
        status: !e.transportation_type
          ? "action_required"
          : e.transportation_type !== "own_vehicle"
            ? "complete"
            : (e.vehicle_info_ok ? "complete" : "action_required"),
        statusTestId: "check-vehicle-status",
        meta: vehicle?.transportation_type
          ? `Transport: ${vehicle.transportation_type}${vehicle.make ? ` · ${vehicle.year || ""} ${vehicle.make} ${vehicle.model || ""}` : ""}`
          : "Not declared",
        actionLabel: (!e.transportation_type || (e.transportation_type === "own_vehicle" && !e.vehicle_info_ok)) ? "Update vehicle info" : null,
        onAction: (!e.transportation_type || (e.transportation_type === "own_vehicle" && !e.vehicle_info_ok)) ? goRisk : null,
      },
      {
        testid: "check-dl",
        Icon: UserRound,
        title: "Driver's license",
        description: e.drivers_license_required
          ? "Required because you selected 'own vehicle' as your transportation type."
          : "Not required for your selected transportation type.",
        status: !e.drivers_license_required
          ? "complete"
          : e.drivers_license_active
            ? (dlDays !== null && dlDays <= 30 ? "expiring_soon" : "complete")
            : (vehicle?.dl_status === "pending" ? "pending" : "action_required"),
        statusTestId: "check-dl-status",
        meta: e.drivers_license_expiration
          ? `Status: ${e.drivers_license_status || "—"} · expires ${e.drivers_license_expiration}${dlDays !== null ? ` (${dlDays}d)` : ""}`
          : "Not submitted",
        actionLabel: e.drivers_license_required && !e.drivers_license_active ? "Update driver's license" : null,
        onAction: e.drivers_license_required && !e.drivers_license_active ? goRisk : null,
      },
      {
        testid: "check-insurance",
        Icon: ShieldIcon,
        title: "Insurance",
        description: e.auto_insurance_required
          ? "Valid auto insurance is required to accept transportation jobs. Uploaded proof is reviewed by CLEANBAZ."
          : "Auto insurance not required for your selected transportation type.",
        status: !e.auto_insurance_required
          ? "complete"
          : e.auto_insurance_active
            ? (insDays !== null && insDays <= 30 ? "expiring_soon" : "complete")
            : "action_required",
        statusTestId: "check-insurance-status",
        meta: activeIns
          ? `${activeIns.carrier} · expires ${activeIns.expiration_date}${insDays !== null ? ` (${insDays}d)` : ""}`
          : "No approved policy on file",
        actionLabel: e.auto_insurance_required && !e.auto_insurance_active ? "Upload insurance" : (e.auto_insurance_active && insDays !== null && insDays <= 30 ? "Renew insurance" : null),
        onAction: (e.auto_insurance_required && !e.auto_insurance_active) || (e.auto_insurance_active && insDays !== null && insDays <= 30) ? goRisk : null,
      },
      {
        testid: "check-emergency",
        Icon: PhoneCall,
        title: "Emergency contact",
        description: "CLEANBAZ admin uses this only in the event of an on-job safety incident.",
        status: e.emergency_contact_ok ? "complete" : "action_required",
        statusTestId: "check-emergency-status",
        meta: emergency?.name ? `${emergency.name}${emergency.relationship ? ` · ${emergency.relationship}` : ""} · ${emergency.phone || "no phone"}` : "Not on file",
        actionLabel: e.emergency_contact_ok ? null : "Add emergency contact",
        onAction: e.emergency_contact_ok ? null : goRisk,
      },
      {
        testid: "check-photos",
        Icon: Camera,
        title: "Before / after photo requirement",
        description: "Every job requires Before and After photos uploaded from the app before you can start or complete a job. Photos protect both parties in disputes.",
        status: "info",
        statusTestId: "check-photos-status",
        meta: "Enforced automatically on job start / completion",
        actionLabel: null,
        onAction: null,
      },
      {
        testid: "check-safety-procedures",
        Icon: ShieldCheck,
        title: "Safety procedures",
        description: "Wear PPE, follow chemical labels, do not mix cleaners, use marked wet-floor signs, and confirm the site is safe to enter before starting.",
        status: "info",
        statusTestId: "check-safety-procedures-status",
        meta: "Reference — no acceptance required",
        actionLabel: null,
        onAction: null,
      },
      {
        testid: "check-property-rules",
        Icon: ShieldIcon,
        title: "Property & customer safety rules",
        description: "Respect the customer's home: no personal photos, no unauthorized areas, no substances or paraphernalia on-site, and no interaction with minors without a guardian present.",
        status: "info",
        statusTestId: "check-property-rules-status",
        meta: "Required behaviour on every job",
        actionLabel: null,
        onAction: null,
      },
      {
        testid: "check-incident-reporting",
        Icon: Siren,
        title: "Incident / damage reporting",
        description: "Vehicle accident, injury, property damage, theft accusation, or customer confrontation must be reported within 24 hours from the Safety & Compliance page.",
        status: "info",
        statusTestId: "check-incident-reporting-status",
        actionLabel: "Report an incident",
        onAction: () => nav("/cleaner/onboarding-checklist"),
      },
      {
        testid: "check-emergency-guidance",
        Icon: PhoneCall,
        title: "Emergency / safety guidance",
        description: "Call 911 immediately for medical emergencies or if you feel unsafe. After you're safe, contact CLEANBAZ support via Help.",
        status: "info",
        statusTestId: "check-emergency-guidance-status",
        actionLabel: "Open Help",
        onAction: () => nav("/support"),
      },
    ];

    return rows;
  }, [eligibility, agreementStatus, insurance, vehicle, emergency, nav]);

  if (loading || !eligibility) {
    return <div className="max-w-3xl mx-auto p-12 text-slate-500" data-testid="checklist-loading">Loading safety checklist…</div>;
  }

  const openCount = items.filter((r) => r.status === "action_required" || r.status === "pending" || r.status === "expiring_soon").length;
  const completeCount = items.filter((r) => r.status === "complete").length;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="cleaner-safety-checklist">
      <div className="mb-6">
        <button onClick={() => nav("/cleaner")} className="text-xs font-mono uppercase tracking-widest text-teal-600 hover:text-teal-700 inline-flex items-center gap-1" data-testid="back-to-portal">
          <ArrowLeft className="w-3 h-3" />Back to Portal
        </button>
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-slate-900 mt-2">Safety Checklist</h1>
        <p className="text-sm text-slate-500 mt-1">Status of every safety and compliance requirement — with quick links to the existing systems for anything incomplete.</p>
      </div>

      <Card className={`mb-6 ${eligibility.eligible_for_transport_jobs ? "border-teal-300 bg-teal-50" : "border-amber-300 bg-amber-50"}`} data-testid="checklist-summary">
        <CardContent className="p-5 flex items-start gap-3">
          {eligibility.eligible_for_transport_jobs
            ? <ShieldCheck className="w-6 h-6 text-teal-600 mt-1" />
            : <ShieldAlert className="w-6 h-6 text-amber-600 mt-1" />}
          <div className="flex-1">
            <div className={`font-semibold ${eligibility.eligible_for_transport_jobs ? "text-teal-900" : "text-amber-900"}`}>
              {eligibility.eligible_for_transport_jobs ? "You're cleared for CLEANBAZ jobs" : "Complete the items below to accept jobs"}
            </div>
            <div className="text-sm mt-1 text-slate-700">
              {completeCount} complete · {openCount} open item{openCount === 1 ? "" : "s"}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-3">
        {items.map((it) => (
          <Row key={it.testid} {...it} />
        ))}
      </div>
    </div>
  );
}
